/**
 * useLoadPreviewData - Hook for load profile preview visualization
 *
 * Manages day selection state and transforms load profile data
 * into graph-ready format for the Load Profile tab preview chart.
 *
 * @module hooks/useLoadPreviewData
 */

import { useState, useMemo, useEffect } from 'react';

/**
 * Calculate the day with highest average load
 *
 * @param {number[]|null} grossLoad - 8760-hour load profile
 * @returns {number} Day index (0-364) with highest average load
 */
function calculatePeakDay(grossLoad) {
    if (!grossLoad || grossLoad.length !== 8760) return 0;

    let maxDailyAvg = -Infinity;
    let peakDay = 0;

    for (let day = 0; day < 365; day++) {
        const startIdx = day * 24;
        const dailyLoads = grossLoad.slice(startIdx, startIdx + 24);
        const dailyAvg = dailyLoads.reduce((sum, val) => sum + val, 0) / 24;

        if (dailyAvg > maxDailyAvg) {
            maxDailyAvg = dailyAvg;
            peakDay = day;
        }
    }

    return peakDay;
}

/**
 * Transform load profile day to PowerGraph-compatible format
 *
 * @param {Object} params
 * @param {number[]} params.grossLoad - 8760-hour gross load profile
 * @param {number[]} params.existingSolarProfile - 8760-hour existing solar profile
 * @param {Date|string} params.startDate - Start date of the load year
 * @param {number} params.dayIndex - Day index to transform (0-364)
 * @returns {Object[]} Hourly data for PowerGraph
 */
function transformDayToGraphData({ grossLoad, existingSolarProfile, startDate, dayIndex }) {
    if (!grossLoad || grossLoad.length !== 8760) return [];

    const startHour = dayIndex * 24;
    const endHour = startHour + 24;

    const start = new Date(startDate);
    start.setDate(start.getDate() + dayIndex);

    const hourlyData = grossLoad.slice(startHour, endHour).map((facilityLoad, hourInDay) => {
        const solarProduction = existingSolarProfile[startHour + hourInDay] || 0;
        const netLoad = Math.max(0, facilityLoad - solarProduction); // Grid needs to serve this

        return {
            timestamp: start.getTime() + hourInDay * 3600 * 1000,
            load: facilityLoad, // Total facility load (line)
            solar_to_load: solarProduction, // Solar production (stacked area)
            utility: netLoad, // Net load from grid (stacked area) - PowerGraph expects 'utility' key
        };
    });

    // Add a point at midnight (24:00) to extend chart visually to end of day
    // This duplicates the 23:00 values since hourly data represents start-of-hour readings
    if (hourlyData.length === 24) {
        const lastPoint = hourlyData[23];
        hourlyData.push({
            timestamp: start.getTime() + 24 * 3600 * 1000, // Midnight next day
            load: lastPoint.load,
            solar_to_load: lastPoint.solar_to_load,
            utility: lastPoint.utility,
        });
    }

    return hourlyData;
}

/**
 * Hook for load profile preview visualization
 *
 * @param {Object} loadProfile - Load profile state object from useLoadProfile
 * @returns {Object} Preview state and data
 */
export function useLoadPreviewData(loadProfile) {
    // Day navigation state
    const [loadPreviewDayIndex, setLoadPreviewDayIndex] = useState(0);

    // Find peak day in load profile (use combined loads which includes EV)
    const peakLoadDay = useMemo(() => {
        return calculatePeakDay(loadProfile.loads);
    }, [loadProfile.loads]);

    // Transform selected day to graph data (use combined loads which includes EV)
    const loadPreviewGraphData = useMemo(() => {
        const graphData = transformDayToGraphData({
            grossLoad: loadProfile.loads, // Use combined loads (base + EV)
            existingSolarProfile: loadProfile.existingSolarProfile,
            startDate: loadProfile.startDate,
            dayIndex: loadPreviewDayIndex,
        });

        // Debug: Check if data is flat
        if (graphData.length > 0) {
            const loads = graphData.map(d => d.load);
            const min = Math.min(...loads);
            const max = Math.max(...loads);
            const isFlat = max - min < 1; // Less than 1 kW variation
            if (isFlat) {
                console.warn('[LoadPreview] Flat curve detected!', {
                    dayIndex: loadPreviewDayIndex,
                    loads: loads.slice(0, 5),
                    min,
                    max,
                    totalLoads: loadProfile.loads?.length,
                    sampleLoads: loadProfile.loads?.slice(loadPreviewDayIndex * 24, loadPreviewDayIndex * 24 + 5)
                });
            }
        }

        return graphData;
    }, [
        loadProfile.loads,
        loadProfile.existingSolarProfile,
        loadProfile.startDate,
        loadPreviewDayIndex,
    ]);

    // Initialize to peak day when combined load changes
    useEffect(() => {
        if (loadProfile.loads && loadProfile.loads.length === 8760) {
            setLoadPreviewDayIndex(peakLoadDay);
        }
    }, [peakLoadDay, loadProfile.loads]);

    return {
        loadPreviewDayIndex,
        setLoadPreviewDayIndex,
        peakLoadDay,
        loadPreviewGraphData,
    };
}

export default useLoadPreviewData;
